// Amortization schedule for financed / BHPH deals, computed from the same
// deal model that fills the packet PDFs (see dealPacketForms.js). Mirrors the
// DeskManager schedule: payment number, due date, days in period, payment,
// principal/interest split, running totals, remaining balance.

import { PAYMENT_FREQUENCIES, effectiveDerived } from './dealPacketForms';

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const num = (v) => {
  const x = typeof v === 'string' ? parseFloat(v.replace(/[$,]/g, '')) : parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const daysBetween = (a, b) => (a && b ? Math.round((b - a) / MS_PER_DAY) : 0);

// Advance a due date by one period. Monthly/semi-monthly stay calendar-aware
// (the 11th of each month stays the 11th); weekly frequencies add fixed days.
export function advanceDueDate(date, frequency, periodIndex, firstDate) {
  const d = new Date(firstDate.getTime());
  switch (frequency) {
    case 'WEEKLY':
      d.setDate(d.getDate() + 7 * periodIndex);
      return d;
    case 'BI-WEEKLY':
      d.setDate(d.getDate() + 14 * periodIndex);
      return d;
    case 'SEMI-MONTHLY': {
      const months = Math.floor(periodIndex / 2);
      d.setMonth(d.getMonth() + months);
      if (periodIndex % 2 === 1) d.setDate(d.getDate() + 15);
      return d;
    }
    case 'MONTHLY':
    default:
      d.setMonth(d.getMonth() + periodIndex);
      return d;
  }
}

/**
 * Build the row-by-row schedule.
 * Options:
 *   includeCashDown       – prepend the cash down as a row 0 at the sale date
 *   includeDeferredDowns  – prepend each deferred down payment as its own row
 * Down-payment rows are display-only: they were subtracted before the amount
 * financed, so they never touch the amortized balance.
 */
export function buildAmortizationSchedule(model, options = {}) {
  const d = effectiveDerived(model);
  const m = model.money;
  const freq =
    PAYMENT_FREQUENCIES.find((f) => f.value === m.paymentFrequency) || PAYMENT_FREQUENCIES[0];

  const nPay = Math.max(Math.round(num(m.numPayments)), 0);
  const amountFinanced = num(d.amountFinanced);
  const paymentAmount = num(d.paymentAmount);
  const finalPayment = num(m.finalPaymentAmount);
  const r = num(m.apr) / 100 / freq.periodsPerYear;

  const saleDate = parseISO(model.saleDate) || new Date();
  const firstDue = parseISO(m.firstPaymentDate) || advanceDueDate(null, freq.value, 1, saleDate);

  const rows = [];

  if (options.includeCashDown && num(m.cashDown) > 0) {
    rows.push({
      n: 0, label: 'CASH DOWN', dueDate: saleDate, days: 0,
      payment: num(m.cashDown), principal: 0, interest: 0, downPayment: true,
    });
  }
  if (options.includeDeferredDowns) {
    (m.deferredDowns || []).forEach((dd, i) => {
      if (num(dd.amount) > 0) {
        rows.push({
          n: 0, label: `DEFERRED DOWN ${i + 1}`, dueDate: parseISO(dd.date) || saleDate,
          days: 0, payment: num(dd.amount), principal: 0, interest: 0, downPayment: true,
        });
      }
    });
  }

  let balance = amountFinanced;
  let totInterest = 0;
  let totPrincipal = 0;
  let totPayments = rows.reduce((s, row) => s + row.payment, 0);
  let prevDate = saleDate;

  for (let i = 1; i <= nPay && balance > 0.005; i++) {
    const dueDate = advanceDueDate(null, freq.value, i - 1, firstDue);
    const interest = round2(balance * r);
    const isLast = i === nPay;
    let payment = isLast && finalPayment > 0 ? finalPayment : paymentAmount;
    let principal = round2(payment - interest);

    if (isLast || principal >= balance) {
      // settle the remaining balance exactly on the last row
      principal = round2(balance);
      payment = round2(principal + interest);
    }

    balance = round2(balance - principal);
    totInterest = round2(totInterest + interest);
    totPrincipal = round2(totPrincipal + principal);
    totPayments = round2(totPayments + payment);

    rows.push({
      n: i, dueDate, days: daysBetween(prevDate, dueDate),
      payment, principal, interest,
      totPrincipal, totInterest, totPayments, balance,
    });
    prevDate = dueDate;
  }

  return {
    rows,
    amountFinanced,
    apr: num(m.apr),
    term: nPay,
    frequency: freq.value,
    totals: { interest: totInterest, principal: totPrincipal, payments: totPayments },
  };
}
